import { isBrowser } from 'browser-or-node';
import NextApp from 'next/app';
import { ApolloProvider } from '@apollo/client';
import SessionReplay, { LDRecord } from '@launchdarkly/session-replay';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';
import React, { useEffect } from 'react';
import useCreateStores from 'state';
import { StyleSheetManager } from 'styled-components';
import { Normalize } from 'styled-normalize';

import { StoresProvider } from 'contexts/stores';
import { ViewportProvider } from 'contexts/viewport';
import Fallback from 'components/fallbacks';
import { useApollo } from 'utils/apollo/client';

import QueryParamProvider from 'utils/query-param-provider';
import { applyRemoteComplianceConfig } from 'shared/core/helpers/compliance-remote-config';
import { setLDClient } from 'shared/observability/ld-client-ref';
import { registerSessionReplayUrlProvider } from 'shared/observability/ld-observability';
import PublicEnv from 'shared/utils/public-env';
import { LD_APPLICATION_CONTEXT } from 'src/constants/launch-darkly';
import { initializeModuleFederation } from 'src/utils/module-federation/runtime-init';
import { getLivePreviewConfig } from 'src/utils/module-federation/get-live-preview-config';
import { defaultUser } from 'shared/constants/users';
import { Session } from 'utils/session';
import { queryStore, QueryStoreProvider } from 'hooks/use-query';
import { detectDeviceType } from 'src/utils/device/detect-device-type';
import { ConsumerShell } from 'src/shells/consumer-shell';

import 'react-multi-carousel/lib/styles.css';

// Want this called immediately so that remotes are available for the first page load
if (isBrowser) {
  initializeModuleFederation(getLivePreviewConfig() ?? window.reactEnv?.remoteExtensions?.theme ?? undefined);
  // Apply the remote compliance config (delivered via react-env) before the app renders.
  applyRemoteComplianceConfig();
}

/**
 * Always-on providers: shared by every route, including lightweight board
 * pages. Heavy consumer chrome lives in ConsumerShell; TV board chrome (none)
 * lives in BoardShell. Adding a new surface type means creating a new shell
 * and adding a branch to the `Shell` constant below.
 *
 * This will wrap every single page component.
 * This will *also* wrap every single storybook component!
 */
function App({ Component, pageProps = {}, deviceType = 'desktop' }) {
  const stores = useCreateStores();
  const apolloClient = useApollo(pageProps.initialApolloState);
  const router = useRouter();
  const ldClient = useLDClient();

  useEffect(() => {
    // Exposes the LD client to non-React modules (Apollo links, identify call sites).
    // No cleanup: App lives for the entire app lifetime and the LD client is a
    // stable singleton, so an unmount-time clear would only create a transient
    // undefined window during React 18 strict-mode rerenders.
    setLDClient(ldClient ?? undefined);
  }, [ldClient]);

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector(`#jss-server-side`);
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  const Shell = Component.shell || ConsumerShell;

  return (
    <QueryParamProvider>
      <StoresProvider value={stores}>
        <StyleSheetManager>
          <ViewportProvider>
            <Normalize />
            {router?.isFallback && <Fallback />}
            {!router?.isFallback && (
              <ApolloProvider client={apolloClient}>
                <QueryStoreProvider value={queryStore}>
                  <Shell Component={Component} pageProps={pageProps} deviceType={deviceType} />
                </QueryStoreProvider>
              </ApolloProvider>
            )}
          </ViewportProvider>
        </StyleSheetManager>
      </StoresProvider>
    </QueryParamProvider>
  );
}

const context = {
  kind: 'multi',
  user: isBrowser ? { ...defaultUser, key: Session.getInstance().data.id } : defaultUser,
  application: LD_APPLICATION_CONTEXT,
};

if (isBrowser) {
  if (window.reactEnv?.dispensaryId) {
    context['ecomm-dispensary'] = {
      key: window.reactEnv.dispensaryId,
    };
  }
  if (window.reactEnv?.enterpriseId) {
    context['ecomm-enterprise'] = {
      key: window.reactEnv.enterpriseId,
    };
  }
}

const sessionReplayPlugins =
  isBrowser && (PublicEnv.appEnv === 'production' || PublicEnv.appEnv === 'stage')
    ? [new SessionReplay({ privacySetting: 'default' })]
    : undefined;

if (sessionReplayPlugins) {
  registerSessionReplayUrlProvider(() => LDRecord.getSession()?.urlWithTimestamp);
}

// Static LD options shared by every request. The per-request `bootstrap` (server-evaluated flags)
// is injected at render time in Root below, so we can't bake it into a module-level config.
const ldOptions = {
  sendLDHeaders: false, // Disable custom headers to eliminate OPTIONS preflight for CORS
  // evaluationReasons adds a reason object to each flag evaluation response, used by LD
  // Experimentation to validate random assignment. Disabling reduces relay payload size.
  // IMPORTANT: Re-enable (set to true) before running any client-side LaunchDarkly experiment.
  // The only consumer of reason.inExperiment is useLaunchDarklyExperiment
  // (apps/marketplace/src/hooks/use-launch-darkly-experiment.ts), which has no production callers
  // today — so this is safe to ship. Flip back to true before adding any new experiment callers.
  evaluationReasons: false,
  flushInterval: 30000,
  // Configure relay proxy endpoints (SDK v3.x uses baseUrl, streamUrl, eventsUrl)
  ...(PublicEnv.launchDarklyBaseUri && { baseUrl: PublicEnv.launchDarklyBaseUri }),
  ...(PublicEnv.launchDarklyStreamUri && { streamUrl: PublicEnv.launchDarklyStreamUri }),
  ...(PublicEnv.launchDarklyEventsUri && { eventsUrl: PublicEnv.launchDarklyEventsUri }),
  ...(sessionReplayPlugins && { plugins: sessionReplayPlugins }),
};

// We render LDProvider here (instead of the module-level withLDProvider HOC) so the `bootstrap`
// can come from this request's server-evaluated flags (props.ldBootstrap). LDProvider's constructor
// seeds flags from an object bootstrap on BOTH server and client, which makes useFlags() correct
// DURING SSR — keeping the server HTML and the first client render in sync (no flag-driven
// hydration mismatch) and letting _document stay purely structural. App stays a child so its
// useLDClient()/useFlags() calls resolve against this provider.
function Root({ ldBootstrap, ...props }) {
  const bootstrap = ldBootstrap ?? 'localStorage';
  return (
    <LDProvider
      clientSideID={PublicEnv.launchDarklyClientId}
      timeout={2} // 2 seconds timeout
      context={context}
      reactOptions={{ useCamelCaseFlagKeys: false }}
      options={{ ...ldOptions, bootstrap }}
    >
      <App {...props} />
    </LDProvider>
  );
}

Root.getInitialProps = async (appContext) => {
  const appProps = await NextApp.getInitialProps(appContext);
  const { req, res } = appContext?.ctx ?? {};
  // Detect the device from the User-Agent so the first (server) render picks the correct responsive
  // layout. Without this, SSR always assumed a mobile viewport, so desktop visitors (e.g. with JS
  // disabled) saw the mobile view until JS hydrated.
  const userAgent = req?.headers['user-agent'] ?? (isBrowser ? window.navigator?.userAgent : undefined);

  // Flags are evaluated once per page request by the Express server (server.js -> res.locals.ldBootstrap)
  // and merely read here. Keeping the Node LD server SDK in server.js (never client-bundled) means
  // _app stays free of server-only imports. Next serializes this into __NEXT_DATA__, so the client
  // hydrates with the identical flag set the server rendered with — no window.__LD_Bootstrap global,
  // no flag-driven hydration mismatch. Falls back to 'localStorage' in Root when null.
  const ldBootstrap = res?.locals?.ldBootstrap ?? null;

  return { ...appProps, deviceType: detectDeviceType(userAgent), ldBootstrap };
};

export default Root;
